/**
 * The cycle's beta-hCG measurements, drawn the same way on every modality.
 *
 * Fed by the canonical cycle series, so an FET, IVF, IUI or OI cycle renders
 * one list from one contract (#3490). What surrounds it differs — an FET cycle
 * also has an order state and the clinic's interpretation to show — but the
 * numbers themselves are drawn here and nowhere else.
 *
 * Reads only, and says only what the record says: a row with no value prints
 * no number, and a draw whose collection time was never recorded says so
 * rather than borrowing the time the result was reported.
 */

import { View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { CardSurface } from "@louise/moon-ui";
import { EMBER } from "../../../../../onboarding/theme";
import { useTranslations, useLocale, type Locale } from "@src/packages/i18n";
import { formatDate, formatTime } from "@src/packages/i18n/formatters";
import {
  hasBetaHcgValue,
  type BetaHcgCollectionTime,
  type BetaHcgDisplayRow,
} from "@src/packages/cycles/presenters/betaHcgSeries";

/**
 * A day, written out.
 *
 * Parsed at local midnight — `new Date("2026-08-24")` is UTC midnight, which
 * renders as the day before west of Greenwich.
 */
export function formatBetaDay(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const date = new Date(`${iso}T00:00:00`);
  if (isNaN(date.getTime())) return "—";
  return formatDate(date, locale, {
    weekday: "short",
    month: "long",
    day: "numeric",
  });
}

function formatCollectionTime(
  collectedAt: BetaHcgCollectionTime,
  locale: Locale,
): string | null {
  switch (collectedAt.kind) {
    case "unknown":
      return null;
    case "day":
      return formatBetaDay(collectedAt.date, locale);
    case "month": {
      const date = new Date(collectedAt.iso);
      if (isNaN(date.getTime())) return null;
      return formatDate(date, locale, { month: "long", year: "numeric" });
    }
    case "year": {
      const date = new Date(collectedAt.iso);
      if (isNaN(date.getTime())) return null;
      return formatDate(date, locale, { year: "numeric" });
    }
    case "dateTime": {
      const date = new Date(collectedAt.iso);
      if (isNaN(date.getTime())) return null;
      const day = formatDate(date, locale, {
        weekday: "short",
        month: "long",
        day: "numeric",
      });
      // `formatDate` is `toLocaleDateString`, which adds the date back when the
      // options carry no date field, so the clock time is asked for separately.
      const time = formatTime(date, locale, {
        hour: "numeric",
        minute: "2-digit",
      });
      return `${day}, ${time}`;
    }
  }
}

const CAPTION_STYLE = {
  fontSize: 11,
  lineHeight: 16,
  color: EMBER.colors.textFaint,
} as const;

export function BetaHcgSeriesList({ rows }: { rows: BetaHcgDisplayRow[] }) {
  const t = useTranslations();
  const { locale } = useLocale();

  return (
    <View testID="beta-hcg-series" style={{ gap: 6 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
          fontWeight: "700",
        }}
      >
        {t("care.treatment_hub.waiting_phase.beta_order_result_series")}
      </Text>
      {rows.map((row) => {
        const collected = formatCollectionTime(row.collectedAt, locale);
        const value = row.value == null ? null : String(row.value);

        return (
          <View key={row.id} style={{ gap: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
              }}
            >
              {hasBetaHcgValue(row)
                ? t(
                    row.sequence == null
                      ? "care.treatment_hub.waiting_phase.beta_order_result_entry_unsequenced"
                      : "care.treatment_hub.waiting_phase.beta_order_result_entry",
                    {
                      params: {
                        sequence: String(row.sequence),
                        value: value ?? (row.rawValue as string),
                        unit: row.unit ?? "",
                      },
                    },
                  )
                : t(
                    "care.treatment_hub.waiting_phase.beta_order_result_no_value",
                  )}
            </Text>
            {/* What she actually typed, when the parsed number loses it — "<1"
                is a result, and "1" is not the same claim. */}
            {value != null && row.rawValue ? (
              <Text className="font-dm-sans" style={CAPTION_STYLE}>
                {t("care.treatment_hub.waiting_phase.beta_order_result_raw", {
                  params: { raw: row.rawValue },
                })}
              </Text>
            ) : null}
            <Text className="font-dm-sans" style={CAPTION_STYLE}>
              {collected
                ? t(
                    "care.treatment_hub.waiting_phase.beta_order_result_collected_date",
                    { params: { date: collected } },
                  )
                : t(
                    "care.treatment_hub.waiting_phase.beta_order_result_collected_unknown",
                  )}
            </Text>
            {/* The report date is shown in its own right, never as a stand-in
                for a collection time the record does not have. */}
            {!collected && row.issuedAt ? (
              <Text className="font-dm-sans" style={CAPTION_STYLE}>
                {t("care.treatment_hub.waiting_phase.beta_order_result_date", {
                  params: {
                    date: formatBetaDay(row.issuedAt.slice(0, 10), locale),
                  },
                })}
              </Text>
            ) : null}
            {row.isCorrection ? (
              <Text className="font-dm-sans" style={CAPTION_STYLE}>
                {t(
                  "care.treatment_hub.waiting_phase.beta_order_result_corrected",
                )}
              </Text>
            ) : null}
            {row.reviewStatus === "verified" ? (
              <Text className="font-dm-sans" style={CAPTION_STYLE}>
                {t(
                  "care.treatment_hub.waiting_phase.beta_order_result_verified",
                )}
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

/**
 * The same measurements on a cycle that has no FET order to hang them off.
 *
 * An IVF, IUI or OI beta used to reach the patient only as an undifferentiated
 * lab row, or as the first line of a free-text outcome note. It is the same
 * clinical fact as the FET one and now comes from the same series, so it gets
 * the same card — minus the order state and interpretation, which are FET
 * workflow and genuinely do not exist here.
 */
export function BetaHcgSeriesCard({ rows }: { rows: BetaHcgDisplayRow[] }) {
  const t = useTranslations();

  // The Outcome panel already offers "Record your result". History adds
  // information only once a measurement exists; don't repeat its empty state.
  if (rows.length === 0) return null;

  return (
    <CardSurface testID="beta-hcg-series-card" style={{ padding: 16, gap: 8 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 20,
          color: EMBER.colors.text,
          fontWeight: "800",
        }}
      >
        {t("care.treatment_hub.waiting_phase.beta_series_title")}
      </Text>
      <BetaHcgSeriesList rows={rows} />
    </CardSurface>
  );
}
